// src/components/pages/Candidates.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import storageService from '../../services/storageService';
import './Candidates.css';

const Candidates = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSkills, setFilterSkills] = useState('all');
  const [filterExperience, setFilterExperience] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    skills: '',
    experience: '',
    currentCompany: '',
    noticePeriod: '',
    expectedSalary: '',
    location: '',
    resumeUrl: '',
    status: 'Active'
  });

  useEffect(() => {
    loadCandidates();
  }, []);

  const loadCandidates = () => {
    const allCandidates = storageService.getCandidates();
    setCandidates(allCandidates);
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingCandidate) {
      storageService.updateCandidate(editingCandidate.id, formData);
    } else {
      storageService.createCandidate(formData);
    }
    
    resetForm();
    loadCandidates();
    setShowModal(false);
  };

  const handleEdit = (candidate) => {
    setEditingCandidate(candidate);
    setFormData(candidate);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this candidate?')) {
      storageService.deleteCandidate(id);
      loadCandidates();
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      skills: '',
      experience: '',
      currentCompany: '',
      noticePeriod: '',
      expectedSalary: '',
      location: '',
      resumeUrl: '',
      status: 'Active'
    });
    setEditingCandidate(null);
  };

  // Filter candidates
  const filteredCandidates = candidates.filter(candidate => {
    const matchesSearch = 
      candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate.skills.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate.currentCompany.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSkills = filterSkills === 'all' || 
      candidate.skills.toLowerCase().includes(filterSkills.toLowerCase());
    
    const matchesExperience = filterExperience === 'all' || 
      candidate.experience === filterExperience;
    
    return matchesSearch && matchesSkills && matchesExperience;
  });

  // Extract unique skills and experience levels
  const allSkills = [...new Set(candidates.flatMap(c => c.skills.split(',').map(s => s.trim())))].filter(Boolean);
  const experienceLevels = [...new Set(candidates.map(c => c.experience))].filter(Boolean);

  const activeCandidates = candidates.filter(c => c.status === 'Active').length;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading candidates...</p>
      </div>
    );
  }

  return (
    <div className="candidates-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Candidates</h1>
          <p>Manage candidate profiles and information</p>
        </div>
        <button onClick={() => setShowModal(true)} className="btn-primary">
          <span className="btn-icon">+</span> Add Candidate
        </button>
      </div>

      {/* Stats */}
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-icon">👤</div>
          <div className="stat-content">
            <h3>{candidates.length}</h3>
            <p>Total Candidates</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{activeCandidates}</h3>
            <p>Active Candidates</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💼</div>
          <div className="stat-content">
            <h3>{allSkills.length}</h3>
            <p>Unique Skills</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search candidates by name, email, skills, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>
        
        <div className="filter-controls">
          <select 
            value={filterSkills} 
            onChange={(e) => setFilterSkills(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Skills</option>
            {allSkills.map(skill => (
              <option key={skill} value={skill}>{skill}</option>
            ))}
          </select>
          
          <select 
            value={filterExperience} 
            onChange={(e) => setFilterExperience(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Experience</option>
            {experienceLevels.map(exp => (
              <option key={exp} value={exp}>{exp}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Candidates Table */}
      <div className="table-container">
        {filteredCandidates.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Skills</th>
                <th>Experience</th>
                <th>Current Company</th>
                <th>Notice Period</th>
                <th>Expected Salary</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCandidates.map(candidate => (
                <tr key={candidate.id}>
                  <td>
                    <div className="candidate-cell">
                      <div className="candidate-avatar">
                        {candidate.name.charAt(0)}
                      </div>
                      <div className="candidate-info">
                        <span className="candidate-name">{candidate.name}</span>
                        <div className="candidate-contact">
                          <a href={`mailto:${candidate.email}`} className="email-link">
                            {candidate.email}
                          </a>
                          <span className="candidate-phone">{candidate.phone}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="skills-cell">
                      {candidate.skills.split(',').slice(0, 3).map((skill, idx) => (
                        <span key={idx} className="skill-tag">{skill.trim()}</span>
                      ))}
                      {candidate.skills.split(',').length > 3 && (
                        <span className="more-tag">+{candidate.skills.split(',').length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="experience-badge">{candidate.experience}</span>
                  </td>
                  <td>{candidate.currentCompany || 'Not specified'}</td>
                  <td>
                    <span className={`notice-badge ${candidate.noticePeriod?.includes('0') ? 'immediate' : 'standard'}`}>
                      {candidate.noticePeriod || 'Not specified'}
                    </span>
                  </td>
                  <td className="salary-cell">{candidate.expectedSalary || 'Negotiable'}</td>
                  <td>
                    <span className={`status-badge ${candidate.status === 'Active' ? 'active' : 'inactive'}`}>
                      {candidate.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        onClick={() => handleEdit(candidate)} 
                        className="action-btn edit"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      {/* <Link 
                        to={`/candidate/${candidate.id}/applications`} 
                        className="action-btn view"
                        title="View Applications"
                      >
                        👁️
                      </Link> */}
                      <button 
                        onClick={() => handleDelete(candidate.id)} 
                        className="action-btn delete"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">👤</div>
            <h3>No candidates found</h3>
            <p>Try adding a new candidate or adjust your search filters</p>
            <button onClick={() => setShowModal(true)} className="btn-primary">
              Add Your First Candidate
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingCandidate ? 'Edit Candidate' : 'Add New Candidate'}</h2>
              <button onClick={() => { resetForm(); setShowModal(false); }} className="modal-close">
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter candidate's full name"
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="candidate@email.com"
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91 9876543210"
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="e.g., Mumbai, Bangalore"
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Experience *</label>
                    <input
                      type="text"
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      placeholder="e.g., 3 years, Fresher"
                      required
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Current Company</label>
                    <input
                      type="text"
                      name="currentCompany"
                      value={formData.currentCompany}
                      onChange={handleInputChange}
                      placeholder="Current employer"
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Notice Period</label>
                    <input
                      type="text"
                      name="noticePeriod"
                      value={formData.noticePeriod}
                      onChange={handleInputChange}
                      placeholder="e.g., 30 days, Immediate"
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Expected Salary</label>
                    <input
                      type="text"
                      name="expectedSalary"
                      value={formData.expectedSalary}
                      onChange={handleInputChange}
                      placeholder="e.g., ₹10 LPA, Negotiable"
                      className="form-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Placed">Placed</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Key Skills *</label>
                  <textarea
                    name="skills"
                    value={formData.skills}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="List key skills separated by commas (React, JavaScript, Node.js)"
                    required
                    className="form-textarea"
                  />
                  <small className="form-hint">Separate skills with commas</small>
                </div>
                
                <div className="form-group">
                  <label>Resume URL</label>
                  <input
                    type="url"
                    name="resumeUrl"
                    value={formData.resumeUrl}
                    onChange={handleInputChange}
                    placeholder="https://drive.google.com/..."
                    className="form-input"
                  />
                  <small className="form-hint">Link to candidate's resume</small>
                </div>
              </div>
              
              <div className="modal-footer">
                <button type="submit" className="btn-primary">
                  {editingCandidate ? 'Update Candidate' : 'Add Candidate'}
                </button>
                <button 
                  type="button" 
                  onClick={() => { resetForm(); setShowModal(false); }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Candidates;